import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import { TreeTable, TreeTableSelectionKeysType, TreeTableSelectionEvent } from 'primereact/treetable';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { TreeNode } from 'primereact/treenode';
import Request from '../../../shared/request/Request';
import ToasterHelper from '../../../shared/helpers/toaster.helper';
import TreeNodeHelper from '../../../shared/helpers/tree-node.helper';
import { API } from '../../../shared/constants/API';
import { HttpStatusCode } from '../../../shared/request/HttpStatusCode';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { getItem } from '../../../services/session-storage.service';
import { IUser } from '../../../models/interfaces/user.interface';

function UserInterests() {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [nodesTemp, setNodesTemp] = useState<TreeNode[]>([]);
  const [nodes, setNodes] = useState<TreeNode[]>([]);
  const [node, setNode] = useState<TreeNode>(null);
  const [selectedNodeKeys, setSelectedNodeKeys] = useState<TreeTableSelectionKeysType | string>(null);
  const user = JSON.parse(getItem('user')) as IUser;

  const loadData = useCallback(async () => {
    Request.get(`${API.USERS_SERVICE}/getUserInterests?uid=${user.id}`, {})
      .then((response: any) => {
        if (response.status.toString() === HttpStatusCode.OK) {
          setNodes(TreeNodeHelper.mapNodes(response.body));
          setNodesTemp(TreeNodeHelper.mapNodes(response.body));
        } else {
          if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          }
          return [];
        }
      })
      .catch(error => {
        ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
        console.log(error);
        return [];
      });
  }, [user.id, t]);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      loadData();
    }
  }, [loadData, loaded]);

  const updateNodes = (localNode, localNodes, type) => {
    let newNodes = JSON.parse(JSON.stringify(localNodes));
    let editedNode = TreeNodeHelper.findNodeByKey(newNodes, localNode.key);
    editedNode.data['label'] = localNode.data['label'];
    editedNode.data['active'] = localNode.data['active'];
    editedNode.data['isActive'] = localNode.data['isActive'];

    if (type === 'nodesTemp') {
      setNodesTemp(newNodes);
    } else {
      setNodes(newNodes);
    }
  };

  const updateInterest = options => {
    Request.put(`${API.USERS_SERVICE}/updateUserInterest`, {
      body: {
        userId: parseInt(user.id.toString(), 10),
        interestId: parseInt(options.node.id, 10),
        isActive: options.node.data['isActive']
      }
    })
      .then((response: any) => {
        if (response.status.toString() === HttpStatusCode.OK) {
          updateNodes(node, nodes, 'nodesTemp');
          ToasterHelper.notify(t('USERS.INTERESTS.INTEREST_UPDATED_SUCCESSFULLY'), 'success');
        } else {
          updateNodes(node, nodesTemp, 'nodes');
          if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          }
          return [];
        }
      })
      .catch(error => {
        ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
        console.log(error);
        return [];
      });
  };

  const isNodeActive = (e, options) => {
    let localNode: TreeNode = options.node;
    localNode.data['isActive'] = e.checked;
    localNode.data['active'] = e.checked ? 'True' : 'False';

    setNode(localNode);
    updateNodes(localNode, nodes, 'nodes');
  };

  const inputTextEditor = options => {
    console.log(options);
    if (!options.node.children) {
      return (
        <>
          <div className='flex flex-row'>
            <InputText className='max-w-40' type='text' value={options.rowData[options.field]} disabled={true} />
            <div className='ml-3 mt-3'>{t('USERS.INTERESTS.INTEREST_ACTIVE')}</div>
            <Checkbox
              className='ml-3 mb-3 mr-3 mt-3'
              onChange={e => isNodeActive(e, options)}
              checked={options.node.data['isActive']}></Checkbox>
            {'   '}
            <Button
              type='button'
              className='background button-border-radius cursor-pointer'
              icon='pi pi-save'
              severity='success'
              rounded
              onClick={() => updateInterest(options)}></Button>
          </div>
        </>
      );
    } else {
      return <>{options.rowData[options.field]}</>;
    }
  };

  return (
    <div className='main-div'>
      <ToastContainer />
      <div className='main-content'>
        <div className='col-md-12 flex block'>
          <div className='col-md-12'>
            <h4>{t('USERS.INTERESTS.INTERESTS_LIST')}</h4>
          </div>
        </div>

        <div className='card'>
          <TreeTable
            value={nodes}
            selectionMode='checkbox'
            selectionKeys={selectedNodeKeys}
            onSelectionChange={(e: TreeTableSelectionEvent) => setSelectedNodeKeys(e.value)}
            tableStyle={{ minWidth: '50rem' }}>
            <Column field='label' header='Name' expander editor={inputTextEditor}></Column>
            <Column field='active' header='Is Active'></Column>
          </TreeTable>
        </div>
      </div>
    </div>
  );
}

export default UserInterests;
